'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { featureFlagsApi } from '@/lib/api';

/**
 * Client-side mirror of the server flag registry (apps/api/src/feature-flags).
 *
 * The defaults here are what renders before (and if) the network call lands, so
 * they must stay in sync with `FEATURE_FLAG_DEFINITIONS` on the API — otherwise
 * a hidden nav item flashes on first paint. Server values always win once they
 * arrive, including for keys this file has never heard of.
 */
export const FEATURE_FLAG_DEFAULTS = {
  'nav.feed': false,
  'nav.invoices': false,
  'nav.transactions': false,
  'nav.calendar': false,
  'nav.schedule': false,
  'nav.reservations': false,
  'sales.manualEntry': true,
  'orders.buyerSpend': false,
  // Storefront builder rollout switch (#177). Off until the designed pages go public.
  'storefront.builder': false,
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAG_DEFAULTS;

/**
 * Keyed by string, not `FeatureFlagKey`: a flag added to the API registry has to
 * reach the app even before this file is updated. Keeping only known keys meant
 * a new `defaultEnabled: true` flag shipped as off, silently.
 */
export type FeatureFlagMap = Record<string, boolean>;

const FEATURE_FLAGS_UPDATED_EVENT = 'sweetspot:feature-flags-updated';
const CACHE_TTL_MS = 60_000;

function defaults(): FeatureFlagMap {
  return { ...FEATURE_FLAG_DEFAULTS };
}

function normalize(raw: Record<string, boolean> | undefined): FeatureFlagMap {
  const merged = defaults();
  if (!raw) return merged;
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'boolean') merged[key] = value;
  }
  if (process.env.NODE_ENV !== 'production') {
    const unknown = Object.keys(raw).filter((key) => !(key in FEATURE_FLAG_DEFAULTS));
    if (unknown.length > 0) {
      // Not an error — the value is honoured. It means this file drifted behind
      // the API registry, so the pre-fetch default for those keys is a guess.
      console.warn('[feature-flags] server sent keys missing from FEATURE_FLAG_DEFAULTS:', unknown);
    }
  }
  return merged;
}

interface FetchResult {
  value: FeatureFlagMap;
  /** True when this result was superseded while in flight — do not render it. */
  stale: boolean;
}

let cache: { value: FeatureFlagMap; expiresAt: number } | null = null;
let inFlight: Promise<FetchResult> | null = null;
// Last map we actually received. Unlike `cache` this is never invalidated, so a
// failed refresh falls back to what we last knew rather than to defaults —
// which would render a flag an admin just enabled as off.
let lastKnown: FeatureFlagMap | null = null;
// Bumped whenever the cache is invalidated. A request that started before the
// bump must neither write the cache nor have its result rendered.
let generation = 0;

async function fetchFlags(force = false): Promise<FetchResult> {
  if (!force && cache && cache.expiresAt > Date.now()) {
    return { value: cache.value, stale: false };
  }
  if (!force && inFlight) return inFlight;

  const startedAt = generation;
  const request: Promise<FetchResult> = featureFlagsApi
    .getFlags()
    .then((res) => {
      const value = normalize(res?.flags);
      if (startedAt !== generation) {
        return { value: lastKnown ?? value, stale: true };
      }
      lastKnown = value;
      cache = { value, expiresAt: Date.now() + CACHE_TTL_MS };
      return { value, stale: false };
    })
    .catch(() => {
      // Flags are a nav convenience, not an auth boundary — never block the UI
      // on this call.
      return { value: lastKnown ?? defaults(), stale: startedAt !== generation };
    })
    .finally(() => {
      // Only clear the slot if it is still ours: a stale request settling after
      // a forced refresh would otherwise drop the newer request's slot and the
      // next caller would start a third concurrent GET instead of joining it.
      if (inFlight === request) inFlight = null;
    });

  inFlight = request;
  return request;
}

/**
 * Invalidate the cache and tell any provider mounted in *this* document to
 * re-read. `window.dispatchEvent` does not cross tabs, and the admin screen —
 * the only caller — lives in the (admin) route group, which mounts no provider.
 * So today this mainly bumps the generation so an in-flight read can't write
 * back a pre-toggle map; other tabs pick the change up on their next
 * visibilitychange, TTL expiry, or reload. Cross-tab would need BroadcastChannel.
 */
export function refreshFeatureFlags(): void {
  generation += 1;
  cache = null;
  inFlight = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(FEATURE_FLAGS_UPDATED_EVENT));
  }
}

interface FeatureFlagContextValue {
  flags: FeatureFlagMap;
  /** False once the first fetch has settled (successfully or not). */
  loading: boolean;
  refresh: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  flags: defaults(),
  loading: false,
  refresh: () => {},
});

export function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlagMap>(() => cache?.value ?? defaults());
  const [loading, setLoading] = useState(() => !cache);

  const load = useCallback((force: boolean) => {
    let cancelled = false;
    fetchFlags(force).then(({ value, stale }) => {
      if (cancelled) return;
      // A superseded read must not paint: rendering it would put a flag the
      // admin just turned off back on screen until the next refresh.
      if (!stale) setFlags(value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(false), [load]);

  useEffect(() => {
    // Keep the cancel handle from the latest event-driven read: these are the
    // two paths most likely to still be in flight when the provider unmounts
    // (navigating out of the (app) group), and without this the `cancelled`
    // flag inside `load` is dead code on exactly those paths.
    let cancelCurrent: (() => void) | null = null;
    const run = (force: boolean) => {
      cancelCurrent?.();
      cancelCurrent = load(force);
    };
    const onUpdated = () => run(true);
    // Re-read when the tab comes back into view. Without this an open session
    // keeps its flags until a full reload, which defeats the point of a kill
    // switch: an admin turns a broken feature off and the people already using
    // it never find out. `load(false)` honours the cache TTL, so a quick
    // tab-switch costs nothing.
    const onVisible = () => {
      if (document.visibilityState === 'visible') run(false);
    };
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, onUpdated);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelCurrent?.();
      window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, onUpdated);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({ flags, loading, refresh: () => load(true) }),
    [flags, loading, load],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

/** Whole map — for components that gate several things at once (nav lists). */
export function useFeatureFlags(): FeatureFlagContextValue {
  return useContext(FeatureFlagContext);
}

/** Single flag. Falls back to the code-side default outside a provider. */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useContext(FeatureFlagContext).flags[key];
}
